import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { Box, Button } from "@mui/material";
import { api } from "@ippon/shared-utils";
import { CustomBrandLogo, AuthInput, passwordRules } from "@ippon/shared-auth";
import { getSecureCookie } from "../../utils/authUtils";
import { useRecaptcha } from "../../shared-auth/src/hooks/useRecaptcha";
import { PASSWORD_CHANGE_RECAPTCHA_ACTION } from "./recaptchaActions";

const ChangePassword: React.FC = () => {
  const [loginStatus, setLoginStatus] = useState("");
  const [apiError, setApiError] = useState("");
  const [samePasswordError, setSamePasswordError] = useState("");
  const [incorrectPasswordError, setIncorrectPasswordError] = useState("");
  const { executeRecaptcha } = useRecaptcha();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const iv = searchParams.get("iv");
  const email = searchParams.get("email") || token; // tokenがemailの場合もある

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<any>();

  const oldPasswordValue = watch("old_password");
  const newPasswordValue = watch("new_password");
  const isHasValue = oldPasswordValue && newPasswordValue;

  // tokenとivがない場合はリダイレクト
  useEffect(() => {
    if (!token || !iv) {
      navigate("/login", { replace: true });
    }
  }, [token, iv, navigate]);

  const onSubmit: SubmitHandler<any> = async (data) => {
    // Clear previous errors
    setApiError("");
    setSamePasswordError("");
    setIncorrectPasswordError("");

    // Check that new password differs from old password
    if (data.old_password === data.new_password) {
      setSamePasswordError(
        "新しいパスワードは現在のパスワードと異なる必要があります"
      );
      return;
    }

    try {
      const recaptchaToken = await executeRecaptcha(
        PASSWORD_CHANGE_RECAPTCHA_ACTION
      );
      if (!recaptchaToken) {
        setApiError(
          "reCAPTCHAの確認に失敗しました。ページを再読み込みして再度お試しください。"
        );
        return;
      }

      await api.post("/auth/change-password", {
        email,
        iv,
        oldPassword: data.old_password,
        newPassword: data.new_password,
        recaptchaToken,
      });

      // 成功時
      setLoginStatus("success");
    } catch (error: any) {
      setLoginStatus("fail");
      const status = error.status ?? error.response?.status;
      const message = error.response?.data?.message ?? error.message;

      if (status === 401) {
        setIncorrectPasswordError(
          "現在のパスワードまたは変更リンクを確認してください"
        );
      } else if (status === 429) {
        setIncorrectPasswordError(
          "パスワードの試行回数が上限に達しました。時間をおいて再度お試しください"
        );
      } else {
        setApiError(message || "パスワードの更新に失敗しました");
      }
    }
  };

  const navigateToLogin = () => {
    setLoginStatus("");
    const referrerUrl = getSecureCookie("referrer_url");
    if (referrerUrl) {
      window.location.href = referrerUrl;
    } else {
      window.location.href = "https://sales-brokerage.ippon-cloud.com/";
    }
  };

  return (
    <Box className="container">
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        autoComplete="off"
        className="formContainer"
      >
        <Box className="fieldContainer">
          <Box className="logoContainer">
            <CustomBrandLogo />
          </Box>
          {loginStatus === "success" ? (
            <>
              <p
                style={{
                  fontSize: "12px",
                  fontWeight: "400",
                  lineHeight: "1.5",
                  textAlign: "center",
                  margin: "16px 0",
                }}
              >
                新しいパスワードでアカウントから
                <br />
                ログインできます。
              </p>
              <Button
                type="button"
                disableElevation
                onClick={navigateToLogin}
                variant="contained"
                className="button"
              >
                ログイン
              </Button>
            </>
          ) : (
            <>
              <Box className="check_mail_content">
                <p className="check_mail_title">パスワード再発行</p>
              </Box>
              <Box className="inputContainer">
                <AuthInput
                  label="現在のパスワード"
                  name="old_password"
                  placeholder="現在のパスワードを入力してください"
                  type="password"
                  register={register}
                  error={!!errors.old_password || !!incorrectPasswordError}
                  helperText={
                    typeof errors.old_password?.message === "string"
                      ? errors.old_password?.message
                      : incorrectPasswordError || undefined
                  }
                  apiError={apiError}
                  isRequired={true}
                  rules={passwordRules}
                  showPasswordToggle={true}
                />

                <AuthInput
                  label="新しいパスワード"
                  name="new_password"
                  placeholder="新しいパスワードを入力してください"
                  type="password"
                  register={register}
                  showPasswordToggle={true}
                  error={!!errors.new_password || !!samePasswordError}
                  helperText={
                    typeof errors.new_password?.message === "string"
                      ? errors.new_password?.message
                      : samePasswordError || undefined
                  }
                  isRequired={true}
                  rules={passwordRules}
                />
              </Box>
              <Button
                type="submit"
                disableElevation
                variant="contained"
                onMouseDown={(e) => e.preventDefault()}
                className={`button ${!isHasValue ? "buttonDisable" : ""}`}
                disabled={!isHasValue}
                sx={{
                  "&:disabled": {
                    backgroundColor: "#BFE6EF !important",
                  },
                }}
              >
                送信
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChangePassword;
